//create a react functional component in typescript
import {
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
  type FC,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import AppContext from "../utils/appContext";
import { confirmDialog, showErrorToast, showToast } from "./ui/feedback";

interface UserRecord {
  ID: number;
  firstname?: string;
  lastname?: string;
  email?: string;
  groups?: string;
  active?: number;
}

interface userListProps {
  refreshCounter: number;
  groups: Array<any>;
}

const textValue = (value: unknown) => String(value ?? "");

const parseUserGroups = (groups?: string) => {
  if (!groups) {
    return [];
  }

  try {
    const parsedGroups = JSON.parse(groups);
    return Array.isArray(parsedGroups) ? parsedGroups : [];
  } catch {
    return [];
  }
};

const UserList: FC<userListProps> = ({ refreshCounter, groups }) => {
  const [users, setUsers] = useState<Array<UserRecord>>([]);
  const [unfilteredUsers, setUnfilteredUsers] = useState<Array<UserRecord>>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMailUserIds, setLoadingMailUserIds] = useState<Array<number>>(
    [],
  );
  const [selectedUsers, setSelectedUsers] = useState<Array<number>>([]);
  const [allSelected, setAllSelected] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState("all");
  const [availablePermissions, setAvailablePermissions] = useState<Array<any>>(
    [],
  );
  const [searchMessage, setSearchMessage] = useState("");
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "lastname", desc: false },
  ]);
  const context = useContext(AppContext);

  const navigate = useNavigate();
  const getUsers = async () => {
    setLoading(true);

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const storedJWT = localStorage.getItem("jwt");

    user.email &&
      (await fetch(process.env.REACT_APP_API_URL + "/get-users.php", {
        method: "POST",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + storedJWT,
        },
        body: JSON.stringify({
          email: user.email,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          setUsers(data);

          setUnfilteredUsers(data);
          setLoading(false);
        })
        .catch((error) => {
          // Handle network or server errors
          console.error();
          setLoading(false);
        }));
  };
  const selectUser = useCallback(
    (id: number) => {
      if (selectedUsers.includes(id)) {
        let newItems = selectedUsers.filter((item) => item !== id);
        setSelectedUsers(newItems);
        setAllSelected(false);
      } else {
        let newItems = [...selectedUsers, id];
        setSelectedUsers(newItems);
        setAllSelected(newItems.length === users.length);
      }
    },
    [selectedUsers, users.length],
  );
  const selectAll = useCallback(() => {
    if (allSelected) {
      setSelectedUsers([]);
    } else {
      let allItems = users.map((user) => user.ID);
      setSelectedUsers(allItems);
    }
    setAllSelected(!allSelected);
  }, [allSelected, users]);

  const getUserGroups = useCallback(
    (user: UserRecord) =>
      parseUserGroups(user.groups)
        .map((permission: number | string) =>
          groups.find((group) => group.ID === Number(permission)),
        )
        .filter(Boolean),
    [groups],
  );

  const getUserGroupNames = useCallback(
    (user: UserRecord) =>
      getUserGroups(user)
        .map((group) => group.name)
        .join(", "),
    [getUserGroups],
  );

  const sendActivationMail = useCallback(async (id: number) => {
    setLoadingMailUserIds((current) => [...current, id]);

    const storedJWT = localStorage.getItem("jwt");

    await fetch(process.env.REACT_APP_API_URL + "/send-activation-mail.php", {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + storedJWT,
      },
      body: JSON.stringify({
        id: id,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.message === "success") {
          showToast({
            message: "Activation mail sent successfully",
            variant: "success",
          });
        } else if (data.error) {
          showErrorToast(data.error);
        } else {
          showToast({
            message: "Something went wrong, try again.",
            variant: "error",
          });
        }
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        setLoadingMailUserIds((current) =>
          current.filter((loadingId) => loadingId !== id),
        );
      });
  }, []);

  const columns = useMemo<ColumnDef<UserRecord>[]>(
    () => [
      {
        id: "select",
        enableSorting: false,
        header: () => (
          <button
            aria-label={allSelected ? "Deselect all" : "Select all"}
            className={`${
              allSelected
                ? "bg-default border-default"
                : "bg-white border-gray-300"
            } grid p-0.5 content-center inline-block w-5 h-5 border rounded cursor-pointer`}
            onClick={selectAll}
          >
            <span>
              {allSelected && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="white"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m4.5 12.75 6 6 9-13.5"
                  />
                </svg>
              )}
            </span>
          </button>
        ),
        cell: ({ row }) => (
          <button
            aria-label={
              selectedUsers.includes(row.original.ID)
                ? "Deselect user"
                : "Select user"
            }
            className={`${
              selectedUsers.includes(row.original.ID)
                ? "bg-default border-default"
                : "bg-white border-gray-300"
            } grid p-0.5 content-center inline-block w-5 h-5 border rounded cursor-pointer`}
            onClick={(event) => {
              event.stopPropagation();
              selectUser(row.original.ID);
            }}
          >
            <span>
              {selectedUsers.includes(row.original.ID) && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="white"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m4.5 12.75 6 6 9-13.5"
                  />
                </svg>
              )}
            </span>
          </button>
        ),
        meta: { className: "w-[75px] min-w-[75px]" },
      },
      {
        accessorKey: "lastname",
        header: "Last name",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[150px] min-w-[150px]" },
      },
      {
        accessorKey: "firstname",
        header: "First name",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[150px] min-w-[150px]" },
      },
      {
        id: "groups",
        header: "Groups",
        accessorFn: getUserGroupNames,
        cell: ({ row }) => (
          <div className="relative flex content-center justify-start gap-1">
            {getUserGroups(row.original).map((group: any) => (
              <div
                key={`group-colour-${row.original.ID}-${group.ID}`}
                className="w-3 h-3 rounded-full group/groups cursor-pointer transition-all relative"
                style={{ backgroundColor: group.colour }}
              >
                <p className="hidden group-hover/groups:block absolute bottom-4 px-2 py-0.5 rounded bg-white text-xs left-1/2 shadow -translate-x-2/4">
                  {group.name}
                </p>
              </div>
            ))}
          </div>
        ),
        meta: { className: "w-[150px] min-w-[150px]" },
      },
      {
        accessorKey: "email",
        header: "E-mail",
        cell: ({ getValue }) => textValue(getValue()),
        meta: { className: "w-[220px] min-w-[220px]" },
      },
      {
        accessorKey: "active",
        header: "Status",
        cell: ({ row }) =>
          row.original.active === 1 ? (
            <p className="inline-block w-auto px-2 py-0.5 text-xs text-green-500 border border-green-500 rounded-full">
              Active
            </p>
          ) : (
            <p className="inline-block w-auto px-2 py-0.5 text-xs text-red-500 border border-red-500 rounded-full">
              Not active
            </p>
          ),
        meta: { className: "w-[150px] min-w-[150px]" },
      },
      {
        id: "activationMail",
        header: "Send activation mail",
        enableSorting: false,
        cell: ({ row }) => {
          const isSending = loadingMailUserIds.includes(row.original.ID);

          return isSending ? (
            <svg
              className="w-6 h-6 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            <button
              className="flex items-center gap-1 group"
              onClick={(event) => {
                event.stopPropagation();
                sendActivationMail(row.original.ID);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-6 h-6 transition cursor-pointer stroke-gray-400 group-hover:stroke-gray-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
              <span className="text-xs text-gray-400 group-hover:text-gray-500">
                Send mail
              </span>
            </button>
          );
        },
        meta: { className: "w-[220px] min-w-[220px]" },
      },
    ],
    [
      allSelected,
      getUserGroupNames,
      getUserGroups,
      loadingMailUserIds,
      selectedUsers,
      selectAll,
      selectUser,
      sendActivationMail,
    ],
  );

  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  //create the function to create a folder
  const updatePermissionList = (items: Array<any>) => {
    let newPermissions: Array<any> = [];

    Array.isArray(items) &&
      items.length > 0 &&
      items.forEach((item) => {
        let permissions = parseUserGroups(item.groups);
        permissions.forEach((permission: any) => {
          if (
            permission > 1 &&
            !newPermissions.some((item) => item.ID === permission)
          ) {
            let obj = groups.find((group) => group.ID === permission);
            if (obj) {
              newPermissions.push(obj);
            }
          }
        });
      });

    // Sort alphabetically
    newPermissions.sort((a, b) => a.name.localeCompare(b.name));

    // Update state
    setAvailablePermissions(newPermissions);
  };
  const filterByGroup = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearch("");
    setSearchMessage("");
    if (e.target.value === "all") {
      setUsers(unfilteredUsers);
    } else {
      let groupID = parseInt(e.target.value);
      let newUsers = unfilteredUsers.filter((user) => {
        let permissions = parseUserGroups(user.groups).map((permission) =>
          Number(permission),
        );

        return permissions.includes(groupID);
      });

      setUsers(newUsers);
      if (newUsers.length === 0) {
        setSearchMessage("No users found");
      }
    }
    setSelectedPermission(e.target.value);
  };
  const logout = () => {
    localStorage.clear();
    context?.updateLoginStatus(false);
    navigate("/login");
  };
  const deleteSelected = async () => {
    const storedJWT = localStorage.getItem("jwt");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (selectedUsers.length > 0) {
      if (
        await confirmDialog({
          title: "Delete users?",
          message: "Are you sure you want to delete the selected users?",
          confirmLabel: "Delete",
          variant: "danger",
        })
      ) {
        setLoading(true);

        try {
          const promises = selectedUsers.map(async (selectedUser) => {
            const response = await fetch(
              process.env.REACT_APP_API_URL + "/delete-user.php",
              {
                method: "POST",
                mode: "cors",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: "Bearer " + storedJWT,
                },
                body: JSON.stringify({
                  email: user.email,
                  id: selectedUser,
                }),
              },
            );

            const data = await response.json();
            if (data === "Error decoding token: Expired token") {
              logout();
            }

            if (data.error) {
              showErrorToast(data.error);
            } else {
              setSelectedUsers([]);
              setAllSelected(false);
            }
          });

          await Promise.all(promises);
          getUsers();
        } catch (error) {
          console.error("Error deleting users:", error);
        }
      }
    }
  };
  const searchUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
    let search = e.target.value;
    setSearch(search);
    setSearchMessage("");

    if (search !== "") {
      setSelectedPermission("all");
      let newUsers = unfilteredUsers.filter((user) => {
        return (
          textValue(user.firstname)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(user.lastname)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          textValue(user.email).toLowerCase().includes(search.toLowerCase())
        );
      });
      setUsers(newUsers);
      if (newUsers.length === 0) {
        setSearchMessage("No users found");
      }
    } else {
      setUsers(unfilteredUsers);
    }
  };
  const emptySearch = () => {
    setSearch("");
    setSearchMessage("");
    setUsers(unfilteredUsers);
    setSelectedPermission("all");
  };

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCounter]);

  useEffect(() => {
    updatePermissionList(unfilteredUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfilteredUsers]);

  return (
    <div className="fade-in ">
      <div className="pr-2">
        <h1 className="flex flex-wrap min-h-[2rem] mb-2 gap-2  items-center text-xl md:text-2xl">
          Users
        </h1>
      </div>
      <div className="relative items-center justify-between pr-2 mb-3 md:flex md:mb-0 md:pr-7 md:mb-5">
        <div className="flex flex-wrap items-start gap-1 md:order-1">
          <button
            className="flex items-center gap-1 group"
            onClick={() => navigate("/")}
          >
            <span className="text-xs text-gray-400 transition-all md:text-sm group-hover:text-gray-300">
              Home
            </span>
          </button>

          <button className="flex items-center gap-1 ">
            <span className="text-xs text-gray-400 transition-all md:text-sm ">{`>`}</span>
            <span className="text-xs transition-all text-gray-400 md:text-sm max-w-[120px] truncate">
              Users
            </span>
          </button>
        </div>
      </div>
      {Array.isArray(unfilteredUsers) && unfilteredUsers.length > 0 && (
        <div className="flex flex-wrap gap-4 mt-5 mb-10">
          <div className="relative flex items-center content-center gap-1 text-xs transition-all border rounded-md text-default hover:bg-shade">
            <input
              className="py-2.5 px-3 rounded-md outline-none placeholder-gray-400 rounded-md border-suzy-gray"
              type="text"
              value={search}
              placeholder="Search users"
              onChange={searchUsers}
            />
            <div className="absolute right-1 top-1/2 -translate-y-2/4">
              {search.length > 0 && (
                <svg
                  className="w-4 h-4 cursor-pointer"
                  onClick={emptySearch}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              )}
            </div>
            <div className="absolute text-xs text-red-700 left-1 -bottom-5">
              <p>{searchMessage}</p>
            </div>
          </div>
          {availablePermissions.length > 0 && (
            <div className="relative flex content-center text-xs transition-all bg-white text-default ">
              <select
                className="text-xs block relative text-gray-500 cursor-pointer bg-none w-full appearance-none hover:bg-shade border py-2.5 pl-3 pr-7 rounded-md outline-none rounded-md border-suzy-gray"
                name="permissions"
                id="permissions"
                value={selectedPermission}
                onChange={filterByGroup}
              >
                <option value="all">All groups</option>
                {availablePermissions.map((permission) => (
                  <option key={permission.ID} value={permission.ID}>
                    {permission.name}
                  </option>
                ))}
              </select>
              <svg
                className="absolute w-3 h-3 stroke-gray-400 top-3.5 right-2 pointer-events-none"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.30859 1.38867L6.85208 6.93184L12.3953 1.38867"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </div>
          )}
          {selectedUsers.length > 0 && (
            <button
              className="flex content-center py-2.5 px-3 items-center gap-1 text-xs transition-all rounded-md text-default ring-1 ring-inset ring-suzy-gray hover:bg-shade control-button"
              onClick={deleteSelected}
            >
              <span className="text-gray-500">Delete selected</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                className="w-4 stroke-red-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      )}
      {loading ? (
        <div className="flex justify-center">
          <svg
            className="inline-blockmt-5 w-9 h-9 text-accent animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      ) : (
        <div className="max-h-[60vh] max-w-full relative overflow-auto border-b border-slate-100 pb-5">
          <table className="w-full min-w-[1090px] table-fixed border-collapse">
            <thead className="sticky top-0 z-10 bg-white">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-suzy-gray">
                  {headerGroup.headers.map((header) => {
                    const meta = header.column.columnDef.meta as
                      { className?: string } | undefined;
                    const sorted = header.column.getIsSorted();

                    return (
                      <th
                        key={header.id}
                        className={`${meta?.className ?? ""} border-r border-suzy-gray px-6 py-3 text-left align-middle last:border-r-0 text-[14px] font-medium text-gray-400`}
                        scope="col"
                      >
                        {header.isPlaceholder ? null : !header.column.getCanSort() ? (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                        ) : (
                          <button
                            className="flex w-full cursor-pointer items-center justify-between gap-2 text-left text-s font-medium text-gray-400 hover:text-default md:text-sm"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <span className="truncate">
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </span>
                            <span className="w-3 shrink-0 text-[10px]">
                              {sorted === "asc"
                                ? "▲"
                                : sorted === "desc"
                                  ? "▼"
                                  : ""}
                            </span>
                          </button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-suzy-gray transition-colors hover:bg-shade/50"
                >
                  {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as
                      { className?: string } | undefined;
                    const value = flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    );
                    const title = typeof value === "string" ? value : undefined;

                    return (
                      <td
                        key={cell.id}
                        title={title}
                        className={`${meta?.className ?? ""} overflow-hidden px-6 py-3 align-middle text-sm`}
                      >
                        <div className="truncate">{value}</div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {table.getRowModel().rows.length === 0 && (
            <p className="py-8 text-sm text-gray-400">No users found</p>
          )}
        </div>
      )}
    </div>
  );
};
export default UserList;
